import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DIAL_CODE_OPTIONS, type DialCode } from '../../../data/dialCodes';
import styles from './PhoneCodeSelect.module.css';

/**
 * Searchable dial-code picker for the Book Shipment phone field.
 *
 * A native <select> cannot do what this needs: the closed control always renders
 * the selected <option>'s full text, so "+971 United Arab Emirates" truncates to
 * "+971 U…". Here the closed control shows only flag + code, while the open list
 * shows flag + code + country name and can be filtered by either.
 */

const flagSrc = (iso: string, w: 40 | 80) =>
  `https://flagcdn.com/w${w}/${iso.toLowerCase()}.png`;

const Flag: React.FC<{ iso: string; lazy?: boolean }> = ({ iso, lazy }) => (
  <img
    className={styles.flag}
    src={flagSrc(iso, 40)}
    srcSet={`${flagSrc(iso, 80)} 2x`}
    alt=""
    width={20}
    height={14}
    loading={lazy ? 'lazy' : undefined}
  />
);

/** Matches on country name or dial code; a leading `+` in the query is ignored. */
const matches = (d: DialCode, query: string) => {
  const q = query.trim().toLowerCase().replace(/^\+/, '');
  if (!q) return true;
  return (
    d.name.toLowerCase().includes(q) ||
    d.code.replace(/^\+/, '').startsWith(q) ||
    d.iso.toLowerCase() === q
  );
};

type Props = {
  /** The dial code, e.g. `+971`. */
  value: string;
  onChange: (code: string) => void;
  invalid?: boolean;
};

const PhoneCodeSelect: React.FC<Props> = ({ value, onChange, invalid }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLLIElement>(null);

  /* `+1` belongs to both the US and Canada, so a code alone can't identify a
     row — the first match wins. Harmless for the payload (the code is what gets
     submitted), but it means picking "United States" redisplays as "Canada". */
  const selected =
    DIAL_CODE_OPTIONS.find((d) => d.code === value) ?? DIAL_CODE_OPTIONS[0];

  const results = useMemo(
    () => DIAL_CODE_OPTIONS.filter((d) => matches(d, query)),
    [query],
  );

  // Close when the click lands outside the control and its list.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Opening starts from a clean search, focused, highlighting the current pick.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(DIAL_CODE_OPTIONS.findIndex((d) => d.iso === selected.iso));
    searchRef.current?.focus();
  }, [open, selected.iso]);

  // A new query renumbers the list, so the old index would point at a different
  // country (or past the end).
  useEffect(() => {
    if (query) setActive(0);
  }, [query]);

  useEffect(() => {
    if (open) activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open, active, results]);

  const pick = (d: DialCode) => {
    onChange(d.code);
    setOpen(false);
  };

  /** Arrow/Enter/Escape, shared by the button (closed) and the search box (open). */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[active]) pick(results[active]);
    }
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={`${styles.control} ${invalid ? styles.invalid : ''}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country dial code: ${selected.name} ${selected.code}`}
      >
        <Flag iso={selected.iso} />
        <span className={styles.code}>{selected.code}</span>
        <span className={styles.chevron} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.popover}>
          <input
            ref={searchRef}
            type="text"
            className={styles.search}
            placeholder="Search country or code"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Search country dial codes"
          />

          {results.length === 0 ? (
            <p className={styles.empty}>No matching country</p>
          ) : (
            <ul className={styles.list} role="listbox">
              {results.map((d, i) => (
                <li
                  key={d.iso}
                  ref={i === active ? activeRef : undefined}
                  role="option"
                  aria-selected={d.iso === selected.iso}
                  className={`${styles.option} ${i === active ? styles.active : ''}`}
                  onMouseEnter={() => setActive(i)}
                  /* Keep focus in the search box so the blur doesn't close the
                     list before the click registers. */
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(d)}
                >
                  <Flag iso={d.iso} lazy />
                  <span className={styles.optCode}>{d.code}</span>
                  <span className={styles.optName}>{d.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PhoneCodeSelect;
